// TenantsPage — Phase 5 Wave 2.5 (v2-cp migration).
//
// Migrated off the legacy cookie-based `@intelliauth/api-client` to the
// new JWT-Bearer `@intelliauth/api-client-v2-cp`. Endpoint surface used
// here:
//
//   GET    /api/v1/organizations/{id}/tenants
//   POST   /api/v1/organizations/{id}/tenants                      (via CreateTenantDialog)
//   POST   /api/v1/organizations/{id}/tenants/{tenantId}/retry-provisioning
//   POST   /api/v1/organizations/{id}/tenants/{tenantId}/suspend
//   POST   /api/v1/organizations/{id}/tenants/{tenantId}/resume
//   POST   /api/v1/organizations/{id}/tenants/{tenantId}/decommission
//   POST   /api/v1/organizations/{id}/tenants/{tenantId}/recreate
//   DELETE /api/v1/organizations/{id}/tenants/{tenantId}
//
// Action gating: every lifecycle button reads its enabled-state from the
// per-tenant `actionAvailability` DTO (cans_*). The backend is the source
// of truth — never re-derive availability from status. While a mutation
// is in flight on a row, ALL action buttons on that row are forced
// `disabled` to prevent double-click hammering even if the backend
// dedupes.
//
// Scope deltas vs legacy:
//
//   - Tenant DTO is snake_case (admin_email, created_at, deleted_at).
//   - Status set narrows to {pending, active, suspended, deleted}. The
//     legacy provisioning|failed|deleting states aren't on the list
//     response; provisioning detail comes from the dedicated
//     /provisioning-status endpoint (Phase 6 surface). For now we treat
//     `pending` as in-flight and poll the list every 5s while any pending
//     row exists.
//   - Tenant-admin embedded UI is REMOVED per locked decision D8 ("link
//     out to dp-admin-console"). The card now shows an "Open admin
//     console" link to the per-tenant subdomain instead of inviting +
//     listing tenant admins inline.
//   - Plan/quota progress bar is removed — v2-cp orgs don't carry
//     plan/maxTenants yet; will return when Phase 6 wires live plans.

import type { components } from '@intelliauth/api-client-v2-cp/types'
import { Button, Card, ConfirmDialog } from '@intelliauth/ui'
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Globe,
  Loader2,
  Mail,
  PauseCircle,
  PlayCircle,
  Plus,
  RotateCw,
  Server,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import CreateTenantDialog, { REGIONS } from '@/components/CreateTenantDialog'
import { useAuth } from '@/contexts/AuthContext'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'
import { tenantBaseUrl } from '@/lib/domain'

// The list endpoint may include the same actionAvailability hint object
// as the detail endpoint (the OpenAPI list schema doesn't declare it yet
// but the backend ships it). We read it defensively and fall back to
// status-based derivation when absent so the FE keeps working through
// an OpenAPI regeneration lag.
type ActionAvailabilityCp = components['schemas']['ActionAvailability']
type ActionAvailability = ActionAvailabilityCp & {
  // Recreate landed in Phase B+C of the lifecycle work but isn't in the
  // generated types yet. Read as optional boolean.
  can_recreate?: boolean
  // Hard-delete after decommission (`DELETE /tenants/{id}`).
  can_delete?: boolean
}

type Tenant = components['schemas']['Tenant'] & {
  actions?: ActionAvailability
}

// Status-based fallback when the backend doesn't supply actionAvailability.
// Mirrors the matrix the backend's lifecycle engine implements; never
// preferred over a real `actions` object from the API.
function deriveActions(status: string): ActionAvailability {
  switch (status) {
    case 'active':
      return { can_suspend: true, can_decommission: true }
    case 'suspended':
      return { can_resume: true, can_decommission: true }
    case 'failed':
      return { can_retry_provisioning: true, can_decommission: true }
    case 'decommissioning':
      return {}
    case 'decommissioned':
      return { can_recreate: true, can_delete: true }
    case 'pending':
    case 'provisioning':
    default:
      return {}
  }
}

function resolveActions(t: Tenant): ActionAvailability {
  if (t.actions && typeof t.actions === 'object') return t.actions
  return deriveActions(t.status)
}

// The OpenAPI Tenant.status enum says {pending, active, suspended,
// deleted} but the live cp-api also emits `decommissioned` (terminal),
// `provisioning` / `decommissioning` (in-progress), and `failed`.
// Until the OpenAPI is regenerated to widen the enum, we accept any
// string and fall back to "Unknown" for forward compatibility.
const statusConfig: Record<
  string,
  {
    label: string
    bg: string
    text: string
    icon: typeof CheckCircle2
  }
> = {
  active: {
    label: 'Active',
    bg: 'bg-emerald-500/20',
    text: 'text-emerald-400',
    icon: CheckCircle2,
  },
  pending: { label: 'Provisioning', bg: 'bg-blue-500/20', text: 'text-blue-400', icon: Loader2 },
  provisioning: {
    label: 'Provisioning',
    bg: 'bg-blue-500/20',
    text: 'text-blue-400',
    icon: Loader2,
  },
  suspended: {
    label: 'Suspended',
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    icon: AlertCircle,
  },
  failed: { label: 'Failed', bg: 'bg-red-500/20', text: 'text-red-400', icon: AlertCircle },
  decommissioning: {
    label: 'Decommissioning',
    bg: 'bg-orange-500/20',
    text: 'text-orange-400',
    icon: Loader2,
  },
  decommissioned: {
    label: 'Decommissioned',
    bg: 'bg-gray-500/20',
    text: 'text-gray-400',
    icon: Trash2,
  },
  deleted: { label: 'Archived', bg: 'bg-gray-500/20', text: 'text-gray-400', icon: Trash2 },
}

const IN_PROGRESS_STATUSES = new Set(['pending', 'provisioning', 'decommissioning'])

function sanitizeError(err: unknown, fallback: string): string {
  if (err instanceof CpV2ApiError) {
    if (err.status === 503) return 'Service temporarily unavailable. Please try again.'
    if (err.status === 409) return 'This tenant can no longer be deleted in its current state.'
    if (err.status === 404) return 'Tenant not found — it may already be gone.'
    return err.message || fallback
  }
  if (err instanceof Error) return err.message || fallback
  return fallback
}

const UNKNOWN_STATUS = {
  label: 'Unknown',
  bg: 'bg-gray-500/20',
  text: 'text-gray-400',
  icon: AlertCircle,
}

function readErr(err: unknown, fallback: string): string {
  if (err instanceof CpV2ApiError) return err.message
  if (err instanceof Error) return err.message
  return fallback
}

export default function TenantsPage() {
  const { currentOrg } = useAuth()

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const canManage = currentOrg?.role === 'owner' || currentOrg?.role === 'admin'

  const fetchTenants = useCallback(async () => {
    if (!currentOrg) return
    try {
      const env = await cpClient().get('/api/v1/organizations/{id}/tenants', {
        path: { id: currentOrg.id },
      })
      const data = (env as { data: Tenant[] }).data ?? []
      setTenants(data)
    } catch (err) {
      toast.error(readErr(err, 'Failed to load tenants'))
    } finally {
      setLoading(false)
    }
  }, [currentOrg])

  useEffect(() => {
    fetchTenants()
  }, [fetchTenants])

  // Poll while any tenant is still provisioning.
  useEffect(() => {
    const hasInProgress = tenants.some((t) => IN_PROGRESS_STATUSES.has(t.status))

    if (hasInProgress) {
      pollRef.current = setInterval(fetchTenants, 5000)
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [tenants, fetchTenants])

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Server className="h-6 w-6 text-muted-foreground" />
            <h1 className="text-2xl font-semibold text-foreground tracking-tight">Tenants</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your authentication tenants. Each tenant is an isolated auth environment.
          </p>
        </div>
        {canManage && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowCreateDialog(true)}
            leftIcon={Plus}
          >
            Create tenant
          </Button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
        </div>
      ) : tenants.length === 0 ? (
        <EmptyState onCreateClick={canManage ? () => setShowCreateDialog(true) : undefined} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tenants.map((tenant, index) => (
            <TenantCard
              key={tenant.id}
              tenant={tenant}
              canManage={canManage}
              index={index}
              orgId={currentOrg.id}
              orgSlug={currentOrg.slug}
              onRetry={async () => {
                try {
                  await cpClient().post(
                    '/api/v1/organizations/{id}/tenants/{tenantId}/retry-provisioning',
                    {
                      path: { id: currentOrg.id, tenantId: tenant.id },
                    },
                  )
                  toast.success('Provisioning retry started')
                  await fetchTenants()
                } catch (err) {
                  toast.error(readErr(err, 'Failed to retry'))
                }
              }}
              onSuspend={async () => {
                try {
                  await cpClient().post('/api/v1/organizations/{id}/tenants/{tenantId}/suspend', {
                    path: { id: currentOrg.id, tenantId: tenant.id },
                    body: { reason: 'Suspended via cp-org-console' },
                  })
                  toast.success(`Tenant "${tenant.name}" suspended`)
                  await fetchTenants()
                } catch (err) {
                  toast.error(readErr(err, 'Failed to suspend tenant'))
                }
              }}
              onResume={async () => {
                try {
                  await cpClient().post('/api/v1/organizations/{id}/tenants/{tenantId}/resume', {
                    path: { id: currentOrg.id, tenantId: tenant.id },
                    body: {},
                  })
                  toast.success(`Tenant "${tenant.name}" resumed`)
                  await fetchTenants()
                } catch (err) {
                  toast.error(readErr(err, 'Failed to resume tenant'))
                }
              }}
              onDecommission={async () => {
                try {
                  await cpClient().post(
                    '/api/v1/organizations/{id}/tenants/{tenantId}/decommission',
                    {
                      path: { id: currentOrg.id, tenantId: tenant.id },
                      body: { reason: 'Decommissioned via cp-org-console' },
                    },
                  )
                  toast.success(`Tenant "${tenant.name}" decommissioning started`)
                  await fetchTenants()
                } catch (err) {
                  toast.error(readErr(err, 'Failed to decommission tenant'))
                }
              }}
              onRecreate={async () => {
                try {
                  // Recreate endpoint was added in Phase B+C of the
                  // tenant lifecycle work; the typed client may not
                  // know it yet, so we go through the untyped path
                  // until OpenAPI regen catches up.
                  // biome-ignore lint/suspicious/noExplicitAny: untyped path
                  await (cpClient() as any).post(
                    '/api/v1/organizations/{id}/tenants/{tenantId}/recreate',
                    {
                      path: { id: currentOrg.id, tenantId: tenant.id },
                      body: { reason: 'Recreated via cp-org-console' },
                    },
                  )
                  toast.success(`Tenant "${tenant.name}" recreate started`)
                  await fetchTenants()
                } catch (err) {
                  toast.error(readErr(err, 'Failed to recreate tenant'))
                }
              }}
              onDelete={async () => {
                try {
                  await cpClient().delete('/api/v1/organizations/{id}/tenants/{tenantId}', {
                    path: { id: currentOrg.id, tenantId: tenant.id },
                  })
                  toast.success('Tenant deleted')
                  await fetchTenants()
                } catch (err) {
                  toast.error(sanitizeError(err, 'Failed to delete tenant'))
                  throw err
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Create Tenant Dialog */}
      <CreateTenantDialog
        open={showCreateDialog}
        orgId={currentOrg.id}
        onOpenChange={(open) => {
          if (!open) setShowCreateDialog(false)
        }}
        onSuccess={() => {
          setShowCreateDialog(false)
          fetchTenants()
        }}
      />
    </div>
  )
}

// ── Tenant Card ─────────────────────────────────────────────────

function TenantCard({
  tenant,
  canManage,
  index = 0,
  orgSlug,
  onRetry,
  onSuspend,
  onResume,
  onDecommission,
  onRecreate,
  onDelete,
}: {
  tenant: Tenant
  canManage: boolean
  index?: number
  orgId: string
  orgSlug: string
  onRetry: () => Promise<void>
  onSuspend: () => Promise<void>
  onResume: () => Promise<void>
  onDecommission: () => Promise<void>
  onRecreate: () => Promise<void>
  onDelete: () => Promise<void>
}) {
  const [retrying, setRetrying] = useState(false)
  const [recreating, setRecreating] = useState(false)
  const [pendingAction, setPendingAction] = useState<'suspend' | 'resume' | 'decommission' | null>(
    null,
  )
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [busy, setBusy] = useState(false)

  const actions = resolveActions(tenant)
  // Any in-flight mutation on this row forces every action button off.
  // Backend dedupes 409s but the UX should never allow the double-click
  // in the first place.
  const rowBusy = busy || retrying || recreating
  const status = statusConfig[tenant.status] ?? UNKNOWN_STATUS
  const isInProgress = IN_PROGRESS_STATUSES.has(tenant.status)
  const StatusIcon = status.icon

  // Retry/Recreate labelling — Recreate wins when both are surfaced.
  const showRecreate = !!actions.can_recreate
  const showRetry = !!actions.can_retry_provisioning && !showRecreate
  // While the tenant is being decommissioned the backend will reject
  // recreate with 409 until the workflow flips status to
  // `decommissioned`. Surface a disabled affordance with a tooltip so
  // the user knows it's coming.
  // Cast: live cp-api emits `decommissioning|provisioning|failed|decommissioned`
  // which the OpenAPI Tenant.status enum doesn't list yet.
  const tenantStatus = tenant.status as string
  const showRecreatePlaceholder =
    canManage && tenantStatus === 'decommissioning' && !actions.can_recreate

  const handleRetry = async () => {
    setRetrying(true)
    try {
      await onRetry()
    } finally {
      setRetrying(false)
    }
  }

  const handleRecreate = async () => {
    setRecreating(true)
    try {
      await onRecreate()
    } finally {
      setRecreating(false)
    }
  }

  const stateTooltip = `Not available — tenant is ${status.label.toLowerCase()}`

  const tenantUrl = tenantBaseUrl(tenant.slug, orgSlug)

  return (
    <Card
      data-testid="tenant-card"
      data-slug={tenant.slug}
      data-status={tenant.status}
      className="p-5 hover:bg-foreground/[0.04] transition-colors animate-fade-in"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/dashboard/tenants/${tenant.id}`}
              className="text-sm font-semibold text-foreground hover:text-primary truncate transition-colors"
            >
              {tenant.name}
            </Link>
            <span
              data-testid="tenant-status-badge"
              data-status={tenant.status}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition-colors ${status.bg} ${status.text}`}
            >
              <StatusIcon className={`h-3 w-3 ${isInProgress ? 'animate-spin' : ''}`} />
              {status.label}
            </span>
          </div>
          <p className="text-xs text-muted-foreground/70 mt-0.5 font-mono truncate">
            {tenant.slug}
          </p>
        </div>
      </div>

      {/* Region */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
        <Globe className="h-3.5 w-3.5" />
        <span>{REGIONS.find((r) => r.value === tenant.region)?.label ?? tenant.region}</span>
      </div>

      {/* Admin recipients — display mirror.
          Source: cp_api.tenants.admin_emails on the tenant DTO. The
          column is written at provisioning time from CreateTenantRequest
          and is treated as best-effort: in-tenant admin changes (invite
          / remove inside the dp-admin-console) do NOT update this
          mirror — by design. Calling per-tenant aegis or per-tenant DB
          on every list render would couple CP availability to DP and
          add an HTTP/DB round-trip we don't need; the mirror is what
          the saga itself emailed at provisioning, which is the right
          "who started this tenant" signal for the card. The title
          tooltip surfaces the snapshot semantics so an admin who renamed
          themselves inside the tenant isn't surprised the card still
          shows the original recipient. */}
      {tenant.admin_emails && tenant.admin_emails.length > 0 && (
        <div
          className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3 min-w-0"
          title="Initial admin recipients set at provisioning. In-tenant changes do not update this list."
        >
          <Mail className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">
            {tenant.admin_emails[0]}
            {tenant.admin_emails.length > 1 && (
              <span className="text-muted-foreground/60">
                {' '}
                +{tenant.admin_emails.length - 1} more
              </span>
            )}
          </span>
        </div>
      )}

      {/* Primary actions (non-mutating links + Retry/Recreate). */}
      <div className="flex items-center gap-3 mt-auto pt-1 flex-wrap">
        {tenant.status === 'active' && (
          <>
            <a
              href={tenantUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground font-medium transition-colors"
            >
              Preview
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <a
              href={`${tenantUrl}/admin`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary/80 font-medium"
            >
              Open Admin Console
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </>
        )}
        {canManage && (showRetry || showRecreate) && (
          <button
            type="button"
            data-testid={showRecreate ? 'tenant-action-recreate' : 'tenant-action-retry'}
            onClick={showRecreate ? handleRecreate : handleRetry}
            disabled={rowBusy}
            title={rowBusy ? 'Mutation in flight' : ''}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCw className={`h-3.5 w-3.5 ${retrying || recreating ? 'animate-spin' : ''}`} />
            {showRecreate ? 'Recreate' : 'Retry provisioning'}
          </button>
        )}
        {showRecreatePlaceholder && (
          <button
            type="button"
            data-testid="tenant-action-recreate"
            disabled
            title="Available after decommission completes."
            className="inline-flex items-center gap-1 text-xs text-muted-foreground/60 font-medium cursor-not-allowed"
          >
            <RotateCw className="h-3.5 w-3.5" />
            Recreate
          </button>
        )}
      </div>

      {/* Lifecycle actions — Suspend / Resume / Decommission.
          Visibility/disabled gated on actionAvailability so we never
          surface a button the backend will 409. Every button is forced
          disabled while ANY mutation on this row is in flight. */}
      {canManage &&
        (actions.can_suspend ||
          actions.can_resume ||
          actions.can_decommission ||
          tenantStatus === 'provisioning' ||
          tenantStatus === 'decommissioning') && (
          <div className="mt-3 pt-3 border-t border-border flex items-center gap-3 flex-wrap">
            {actions.can_suspend && (
              <button
                type="button"
                data-testid="tenant-action-suspend"
                onClick={() => setPendingAction('suspend')}
                disabled={rowBusy}
                title={rowBusy ? 'Mutation in flight' : ''}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-orange-400 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PauseCircle className="h-3.5 w-3.5" />
                Suspend
              </button>
            )}
            {actions.can_resume && (
              <button
                type="button"
                data-testid="tenant-action-resume"
                onClick={() => setPendingAction('resume')}
                disabled={rowBusy}
                title={rowBusy ? 'Mutation in flight' : ''}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-emerald-400 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlayCircle className="h-3.5 w-3.5" />
                Resume
              </button>
            )}
            {actions.can_decommission ? (
              <button
                type="button"
                data-testid="tenant-action-decommission"
                onClick={() => setPendingAction('decommission')}
                disabled={rowBusy}
                title={rowBusy ? 'Mutation in flight' : ''}
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-400 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Decommission
              </button>
            ) : tenantStatus === 'provisioning' ? (
              <button
                type="button"
                data-testid="tenant-action-decommission"
                disabled
                title="Wait for provisioning to complete before decommissioning."
                className="inline-flex items-center gap-1 text-xs text-muted-foreground/60 font-medium cursor-not-allowed"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Decommission
              </button>
            ) : tenantStatus === 'decommissioning' ? (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/70">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Decommissioning…
              </span>
            ) : null}
          </div>
        )}

      {/* Delete action — only when the backend says so. */}
      {canManage && (actions.can_delete || tenantStatus === 'decommissioned') && (
        <div className="mt-3 pt-3 border-t border-border flex items-center gap-3 flex-wrap">
          <button
            type="button"
            data-testid="tenant-action-delete"
            onClick={() => setShowDeleteDialog(true)}
            disabled={rowBusy}
            title={rowBusy ? 'Mutation in flight' : stateTooltip}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-400 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </button>
        </div>
      )}

      {/* Type-to-confirm delete dialog — DS ConfirmDialog handles portal
          + viewport centering + theme tokens. */}
      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={(open) => !open && setShowDeleteDialog(false)}
        title="Delete tenant"
        description={
          <>
            <span className="block">
              <span className="text-foreground font-medium">{tenant.name}</span> ({tenant.slug})
            </span>
            <span className="block mt-2">
              This permanently removes the tenant from the console. Its data was already erased when
              it was decommissioned. This action cannot be undone.
            </span>
          </>
        }
        typeToConfirm={tenant.slug}
        confirmLabel="Delete tenant"
        destructive
        loading={busy}
        onConfirm={async () => {
          setBusy(true)
          try {
            await onDelete()
            setShowDeleteDialog(false)
          } finally {
            setBusy(false)
          }
        }}
      />

      {/* Confirm dialog for lifecycle actions */}
      <ConfirmDialog
        open={pendingAction !== null}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={pendingAction ? ACTION_COPY[pendingAction].title : ''}
        description={
          pendingAction ? (
            <>
              <span className="block">
                <span className="text-foreground font-medium">{tenant.name}</span> ({tenant.slug})
              </span>
              <span className="block mt-2">{ACTION_COPY[pendingAction].body}</span>
            </>
          ) : undefined
        }
        confirmLabel={pendingAction ? ACTION_COPY[pendingAction].confirm : 'Confirm'}
        destructive={pendingAction ? ACTION_COPY[pendingAction].destructive : false}
        loading={busy}
        onConfirm={async () => {
          if (!pendingAction) return
          setBusy(true)
          try {
            if (pendingAction === 'suspend') await onSuspend()
            if (pendingAction === 'resume') await onResume()
            if (pendingAction === 'decommission') await onDecommission()
          } finally {
            setBusy(false)
            setPendingAction(null)
          }
        }}
      />
    </Card>
  )
}

// ── Confirm Modal for Lifecycle Actions ─────────────────────────

const ACTION_COPY: Record<
  'suspend' | 'resume' | 'decommission',
  { title: string; body: string; confirm: string; destructive: boolean }
> = {
  suspend: {
    title: 'Suspend tenant',
    body:
      'Suspending will block all authentication traffic to this tenant. ' +
      'You can resume at any time with no data loss.',
    confirm: 'Suspend',
    destructive: false,
  },
  resume: {
    title: 'Resume tenant',
    body: 'Resuming will restore authentication traffic to this tenant.',
    confirm: 'Resume',
    destructive: false,
  },
  decommission: {
    title: 'Decommission tenant',
    body:
      'Decommissioning is permanent. The tenant and everything in it — users, ' +
      'sessions, and settings — are erased, and all sign-ins stop immediately. ' +
      'This cannot be undone.',
    confirm: 'Decommission',
    destructive: true,
  },
}

// ── Empty State ─────────────────────────────────────────────────

function EmptyState({ onCreateClick }: { onCreateClick?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-lg p-16">
      <Server className="h-10 w-10 text-muted-foreground/70 mb-4" />
      <h3 className="text-lg font-medium text-foreground mb-1">No tenants yet</h3>
      <p className="text-sm text-muted-foreground text-center max-w-sm mb-6">
        Create your first tenant to set up an isolated authentication environment for your
        application.
      </p>
      {onCreateClick && (
        <Button variant="primary" size="sm" onClick={onCreateClick} leftIcon={Plus}>
          Create your first tenant
        </Button>
      )}
    </div>
  )
}
